import { DixaColorMode } from '@dixa/lib-shared-tokens';
import { electron } from 'lib-shared-electron';
import { ampli, IdentifyProperties } from 'lib-shared-tracking';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useMount } from 'react-use';

import {
  getLocalSettings,
  updateSettings,
} from 'components/settings/api/personal';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

import {
  ColorMode,
  ColorModeContextValue,
  ColorModeProviderProps,
} from './colorMode.types';

const localStorageKey = 'colorMode';

const ColorModeContext = createContext<ColorModeContextValue>(undefined!);

export function ColorModeProvider({
  children,
}: ColorModeProviderProps): JSX.Element {
  const storage = useServerStorage('user');
  const flags = useFeatureFlag(['dark-mode']);
  const { user } = useCurrentUser();

  const isColorModeSupported = useMemo(
    () => flags.darkMode && !electron.isElectron(),
    [flags.darkMode],
  );

  const prefersDarkMode = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  /** The current color mode, resolved either from serverStorage or localStorage. */
  const colorMode: ColorMode = isColorModeSupported
    ? storage.data.global?.colorMode ??
      getLocalSettings()?.[localStorageKey] ??
      'light'
    : 'light';

  /**
   * The effective color mode, which if the user chose 'auto', will be
   * determined by the system's color scheme preference.
   */
  const effectiveColorMode = useMemo(
    (): DixaColorMode =>
      colorMode === 'auto' ? (prefersDarkMode() ? 'dark' : 'light') : colorMode,
    [colorMode, prefersDarkMode],
  );

  useMount(() => {
    applyColorMode(colorMode);

    // Ensures that the value in localStorage is in sync with the serverStorage.
    const localColorMode = getLocalSettings()?.[localStorageKey];
    if (localColorMode !== colorMode) {
      updateSettings({ key: localStorageKey, value: colorMode });
    }
  });

  // Updates the user's color mode in Amplitude whenever it changes.
  useEffect(() => {
    ampli.identify(user.id, {
      colorMode: effectiveColorMode,
    } as IdentifyProperties);
  }, [effectiveColorMode, user.id]);

  /**
   * Applies the specified color mode to the document's HTML element. If the
   * mode is set to 'auto', it checks the system's color scheme preference and
   * applies either 'dark' or 'light' mode accordingly.
   */
  const applyColorMode = useCallback(
    (mode: ColorMode) => {
      const effectiveColorMode =
        mode === 'auto' ? (prefersDarkMode() ? 'dark' : 'light') : mode;

      document.documentElement.setAttribute(
        'data-color-mode',
        effectiveColorMode,
      );
    },
    [prefersDarkMode],
  );

  // Listens to changes in the system's color scheme preference and updates
  // the color mode accordingly. Only relevant if the user has selected 'auto'.
  useEffect(() => {
    if (!isColorModeSupported) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (event: MediaQueryListEvent) => {
      applyColorMode(event.matches ? 'dark' : 'light');
    };

    if (colorMode === 'auto') {
      mediaQuery.addEventListener('change', handleChange);
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [applyColorMode, colorMode, isColorModeSupported]);

  /**
   * Previews a specified color mode by applying it to the document's HTML
   * element. This is useful for providing a live preview of the selected mode
   * before the user confirms their choice.
   */
  const previewColorMode = useCallback(
    (mode: ColorMode) => {
      applyColorMode(mode);
    },
    [applyColorMode],
  );

  /**
   * Persists the specified color mode by updating the serverStorage and
   * localStorage. This ensures that the user's preference is saved and applied
   * across sessions.
   */
  const persistColorMode = useCallback(
    (mode: ColorMode) => {
      applyColorMode(mode);

      updateSettings({ key: localStorageKey, value: mode });

      storage.set({
        global: (draft) => {
          draft.colorMode = mode;
        },
      });
    },
    [applyColorMode, storage],
  );

  /** Toggles the color mode between 'dark' and 'light'. */
  const toggleColorMode = useCallback((): DixaColorMode => {
    const newColorMode = effectiveColorMode === 'dark' ? 'light' : 'dark';
    persistColorMode(newColorMode);
    return newColorMode;
  }, [effectiveColorMode, persistColorMode]);

  const contextValue: ColorModeContextValue = useMemo(
    () => ({
      colorMode,
      effectiveColorMode,
      isColorModeSupported,
      persistColorMode,
      previewColorMode,
      toggleColorMode,
    }),
    [
      colorMode,
      effectiveColorMode,
      isColorModeSupported,
      persistColorMode,
      previewColorMode,
      toggleColorMode,
    ],
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}
